/**
 * 1 品目ぶんの商品知識シート。
 *
 * ## 版は 2 つあり、混ぜない
 *
 * 人手の版（`version`）は人の確定・修正だけが進める楽観ロックで、
 * 生成の版（`generatedBy`）は機械生成のモデル・プロンプト版である。
 * 1 つの「最終更新」に畳むと、機械が入れたのか人が直したのかが読めなくなる。
 * 画面でも別の区画に出す。
 *
 * ## 人手の値は機械生成の値を消さない
 *
 * 人が確定した値を**取り下げる**と、機械生成の値がそのまま戻る
 * （`value` も `values` も送らないのが取り下げ）。だから削除の mutation が無い。
 *
 * ## 断られた理由を必ず出す
 *
 * `userErrors` は `field`（`input.expectedVersion` など）が付いて返る。
 * 入力欄に紐づける口だけに流すと**誰にも表示されないまま消える**
 * （ペルソナの削除で同じ罠を踏んだ）。編集の区画にそのまま出す。
 *
 * ## 版は毎回読み直す
 *
 * 送るたびに人手の版が進むので、`expectedVersion` は必ず読み直した値を添える。
 * 書けたら読み直してから次を送る。
 */

import { useCallback, useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  KnowledgeSheetDocument,
  LoadingState,
  Meter,
  PageHeader,
  Stat,
  Tag,
  UpdateKnowledgeAttributeDocument,
  type KnowledgeSheetFieldsFragment,
  type TagTone,
  type ValueSource,
  type VocabularyChoiceFieldsFragment,
} from "visumo-data-agent-web-shared";

import { formatJstWithZone } from "../format-time";
import { HelpIcon } from "../help/HelpIcon";

type Attribute = KnowledgeSheetFieldsFragment["attributes"][number];
type SheetUserError = {
  field?: readonly string[] | null;
  code: string;
  message: string;
};

const SOURCE_LABEL: Record<ValueSource, string> = {
  MASTER: "商品マスター",
  REVIEW: "レビュー",
  UGC: "UGC",
  VIDEO: "動画",
  INFERRED: "機械生成",
  MANUAL: "人手",
  NONE: "未取得",
};

/** 出所の札の色。人手が入っているものを他と見分けられるようにする */
const SOURCE_TONE: Record<ValueSource, TagTone> = {
  MASTER: "info",
  REVIEW: "neutral",
  UGC: "neutral",
  VIDEO: "neutral",
  INFERRED: "warn",
  MANUAL: "ok",
  NONE: "neutral",
};

/** 自由属性（軸が無い複数値）の入力の区切り */
const FREE_VALUE_SEPARATOR = "・";

/**
 * 値が画像として出せる URL か。
 *
 * **属性の名前では決めない。** `image_url` のような名前を画面が覚えると、
 * 属性が増えるたびに画面側の表を直す約束が増える。値そのものが http(s) の
 * URL かどうかだけを見て、出せなければ URL の文字に戻す。
 */
function httpUrl(value: string): URL | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  return url.protocol === "http:" || url.protocol === "https:" ? url : null;
}

/**
 * URL の値。札（サムネ）と、開ける URL の両方を出す。
 *
 * 画像で無かった・取りに行けなかったときは札を消して URL だけにする。
 * 「画像を出せません」とは書かない（この属性が画像とは限らないので、
 * 読み手に「画像のはずなのに壊れている」と読ませてしまう）。
 */
function UrlValue({ value, label }: { value: string; label: string }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="vd-value-url">
      {broken ? null : (
        <img
          className="vd-product-image vd-product-image--thumb"
          src={value}
          alt={`${label}のサムネ`}
          width={64}
          height={48}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => {
            setBroken(true);
          }}
        />
      )}
      <a href={value} target="_blank" rel="noreferrer noopener">
        {value}
      </a>
    </span>
  );
}

/**
 * いまの値。
 *
 * URL が混じっているときだけ縦に積んで札を付ける。それ以外は従来どおり
 * `・` で繋いだ 1 行にする（語彙の値は 1 行で読むほうが短い）。
 */
export function AttributeValues({
  values,
  label,
}: {
  values: readonly string[];
  label: string;
}) {
  if (values.length === 0) {
    return <>未取得</>;
  }
  if (!values.some((value) => httpUrl(value) !== null)) {
    return <>{values.join(FREE_VALUE_SEPARATOR)}</>;
  }
  return (
    <span className="vd-value-list">
      {values.map((value) =>
        httpUrl(value) === null ? (
          <span key={value}>{value}</span>
        ) : (
          <UrlValue key={value} value={value} label={label} />
        ),
      )}
    </span>
  );
}

function splitFreeValues(text: string): string[] {
  return text
    .split(/[・,、\n]/)
    .map((value) => value.trim())
    .filter((value) => value !== "");
}

/** 語彙の軸を id で引ける形にする */
function axisIndex(
  axes: readonly VocabularyChoiceFieldsFragment[],
): ReadonlyMap<string, VocabularyChoiceFieldsFragment> {
  return new Map(axes.map((axis) => [axis.id, axis]));
}

/**
 * 表示名 → 語の識別子。
 *
 * 画面に出ている値は日本語の表示名だが、API が受けるのは統制語彙の識別子で、
 * 変換の表は語彙そのものにしかない。表に無い表示名は**変換せずに落とす**
 * （推測で識別子を作ると、値域の外の値が入る）。
 */
function toSlugs(
  axis: VocabularyChoiceFieldsFragment | undefined,
  displayValues: readonly string[],
): string[] {
  if (axis === undefined) {
    return [...displayValues];
  }
  const bySlug = new Map(
    axis.terms.edges.map((edge) => [edge.node.term, edge.node.slug]),
  );
  return displayValues
    .map((value) => bySlug.get(value))
    .filter((slug): slug is string => slug !== undefined);
}

function AttributeRow({
  attribute,
  axes,
  version,
  busy,
  onSubmit,
}: {
  attribute: Attribute;
  axes: readonly VocabularyChoiceFieldsFragment[];
  version: number;
  busy: boolean;
  onSubmit: (
    attribute: Attribute,
    values: readonly string[] | null,
    version: number,
  ) => void;
}) {
  const axis =
    attribute.axis === null ? undefined : axisIndex(axes).get(attribute.axis);
  // 単数か複数かは API が言う。値の数から推し量らない
  const multiValued = attribute.multiValued;
  const current = attribute.values;
  const currentSlugs = toSlugs(axis, current);

  // 単数の属性の下書き。読み直すと現在の値から始まる
  const [choice, setChoice] = useState<string>(currentSlugs[0] ?? "");
  // 複数値の属性の下書き
  const [checked, setChecked] = useState<readonly string[]>(currentSlugs);
  // 自由属性（軸が無い）の下書き
  const [free, setFree] = useState<string>(current.join(FREE_VALUE_SEPARATOR));

  const label = attribute.label;
  const manual = attribute.source === "MANUAL";

  return (
    <div
      role="group"
      aria-label={label}
      style={{ borderTop: "1px solid var(--vd-border)", paddingTop: 12 }}
    >
      <div className="vd-row" style={{ justifyContent: "space-between" }}>
        <div className="vd-card-title">
          {label}
          <Tag tone={SOURCE_TONE[attribute.source]}>
            {SOURCE_LABEL[attribute.source]}
          </Tag>
        </div>
        {/*
          いまの値。出所の札（「未取得」）と同じ文字になることがあるので、
          読み上げの名前を付けて指せるようにしておく（通しと試験の両方で使う）
        */}
        <div aria-label={`${label}のいまの値`} className="vd-value-cell">
          <AttributeValues values={current} label={label} />
        </div>
      </div>
      <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
        根拠: {attribute.evidence}
        {/* 確からしさはデータ源に列が無いので常に null。0 % の帯を描かない */}
        {attribute.confidence == null
          ? "（確からしさは測っていません）"
          : ` ・ 確からしさ ${Math.round(attribute.confidence * 100)} %`}
      </p>

      {!attribute.humanEditable ? (
        <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
          この属性はこの画面からは直せません。直す場所は商品マスターです。
        </p>
      ) : (
        <div className="vd-row" style={{ marginTop: 8, flexWrap: "wrap" }}>
          {axis === undefined ? (
            <input
              className="vd-input"
              aria-label={`${label}の値`}
              placeholder={`値（${FREE_VALUE_SEPARATOR} で区切ります）`}
              value={free}
              onChange={(event) => setFree(event.target.value)}
            />
          ) : multiValued ? (
            <fieldset
              className="vd-field"
              style={{ border: 0, margin: 0, padding: 0 }}
            >
              <legend className="vd-field-hint">
                {label}（統制語彙の語から選びます）
              </legend>
              {axis.terms.edges.map((edge) => (
                <label
                  key={edge.node.slug}
                  style={{ marginRight: 12, whiteSpace: "nowrap" }}
                >
                  <input
                    type="checkbox"
                    checked={checked.includes(edge.node.slug)}
                    onChange={(event) =>
                      setChecked((values) =>
                        event.target.checked
                          ? [...values, edge.node.slug]
                          : values.filter((slug) => slug !== edge.node.slug),
                      )
                    }
                  />{" "}
                  {edge.node.term}
                </label>
              ))}
            </fieldset>
          ) : (
            <label className="vd-field">
              <span className="vd-field-hint">
                {label}（統制語彙の語から選びます）
              </span>
              <select
                className="vd-select"
                aria-label={`${label}を選ぶ`}
                value={choice}
                onChange={(event) => setChoice(event.target.value)}
              >
                <option value="">選んでいません</option>
                {axis.terms.edges.map((edge) => (
                  <option key={edge.node.slug} value={edge.node.slug}>
                    {edge.node.term}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Button
            variant="primary"
            disabled={
              busy ||
              (axis === undefined
                ? splitFreeValues(free).length === 0
                : multiValued
                  ? checked.length === 0
                  : choice === "")
            }
            aria-label={`${label}を人手で確定する`}
            onClick={() =>
              onSubmit(
                attribute,
                axis === undefined
                  ? splitFreeValues(free)
                  : multiValued
                    ? checked
                    : [choice],
                version,
              )
            }
          >
            人手で確定する
          </Button>
          {manual ? (
            <Button
              disabled={busy}
              aria-label={`${label}の人手の値を取り下げる`}
              onClick={() => onSubmit(attribute, null, version)}
            >
              人手の値を取り下げる
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function KnowledgeSheetPage() {
  const { productId } = useParams();
  const [userErrors, setUserErrors] = useState<SheetUserError[]>([]);
  const [saved, setSaved] = useState<string | null>(null);
  const [result, refetch] = useQuery({
    query: KnowledgeSheetDocument,
    variables: { productId: productId ?? "" },
    pause: productId === undefined,
  });
  const [updateState, update] = useMutation(UpdateKnowledgeAttributeDocument);

  const reload = useCallback(() => {
    refetch({ requestPolicy: "network-only" });
  }, [refetch]);

  const submit = useCallback(
    (
      attribute: Attribute,
      values: readonly string[] | null,
      version: number,
    ) => {
      void update({
        input: {
          productId: productId ?? "",
          group: attribute.group,
          attribute: attribute.attribute,
          // 取り下げは value も values も送らない（機械生成の値が戻る）
          ...(values === null
            ? {}
            : attribute.multiValued
              ? { values: [...values] }
              : { value: values[0] ?? null }),
          expectedVersion: version,
        },
      }).then((response) => {
        if (response.error !== undefined) {
          setSaved(null);
          setUserErrors([
            {
              field: null,
              code: "INTERNAL_ERROR",
              message: `保存できませんでした: ${response.error.message}`,
            },
          ]);
          return;
        }
        const payload = response.data?.updateKnowledgeAttribute;
        if (payload === undefined) {
          return;
        }
        if (payload.userErrors.length > 0) {
          setSaved(null);
          setUserErrors([...payload.userErrors]);
          return;
        }
        setUserErrors([]);
        setSaved(
          values === null
            ? `${attribute.label} の人手の値を取り下げました。`
            : `${attribute.label} を人手で確定しました。`,
        );
        // 版は毎回読み直す。応答の版を握ったまま次を送らない
        reload();
      });
    },
    [productId, reload, update],
  );

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined) {
    return <ErrorState description={result.error.message} onRetry={reload} />;
  }
  const sheet = result.data?.knowledgeSheet ?? null;
  if (sheet === null) {
    return (
      <div className="vd-page">
        <EmptyState
          title="この商品の知識シートは見つかりませんでした"
          description={<Link to="/products">商品一覧へ戻る</Link>}
        />
      </div>
    );
  }

  const axes = result.data?.vocabularyAxes ?? [];
  const filled = sheet.attributes.filter(
    (attribute) => attribute.values.length > 0,
  ).length;
  const groups = [
    ...new Set(sheet.attributes.map((attribute) => attribute.group)),
  ];
  const conflicted = userErrors.some((error) => error.code === "CONFLICT");

  return (
    <div className="vd-page">
      <PageHeader
        title={sheet.productName ?? "(名前が入っていません)"}
        description="この商品の属性と、その値がどこから来たかです。"
        titleExtra={<HelpIcon slug="knowledge/sheet" />}
        extra={
          <span className="vd-row">
            <Link to={`/products/${sheet.productId}`}>商品詳細へ</Link>
          </span>
        }
      />

      <div className="vd-stack">
        <Card
          title="この商品の充足"
          note="割合は属性の数から出しています。属性群の数から出した下の一覧とは母数が違います。"
        >
          <div className="vd-grid">
            <Stat
              label="埋まっている属性"
              value={`${filled} / ${sheet.attributes.length}`}
              note="値が 1 つ以上入っている属性の数"
            />
            <Stat
              label="人手の版"
              value={String(sheet.version)}
              note={
                sheet.version === 0
                  ? "まだ人が 1 度も触っていません"
                  : `人が最後に確定・修正したのは ${formatJstWithZone(sheet.updatedAt)}`
              }
            />
            <Stat
              label="生成の版"
              value={sheet.generatedBy === null ? "—" : "あり"}
              note={
                sheet.generatedBy === null
                  ? "この商品はまだ機械生成にかかっていません"
                  : `${sheet.generatedBy.modelId} / プロンプト ${sheet.generatedBy.promptVersion}`
              }
            />
          </div>
          <div style={{ marginTop: 12 }}>
            <Meter ratio={sheet.coverage ?? null} />
          </div>
          {sheet.generatedBy === null ? null : (
            <p className="vd-stat-note" style={{ marginBottom: 0 }}>
              機械生成した時刻:{" "}
              {formatJstWithZone(sheet.generatedBy.generatedAt)}
            </p>
          )}
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            人手の版と生成の版は別のものです。機械生成をやり直しても人手の版は
            進まず、人が直しても生成の版は変わりません。
          </p>
        </Card>

        {filled === 0 ? (
          <div className="vd-notice" role="status">
            <p style={{ margin: 0 }}>
              <strong>この商品の属性はまだ 1 件もありません。</strong>
              機械生成の結果がまだ無く、人手で確定した値もありません。
            </p>
          </div>
        ) : null}

        <Card
          title="属性の値と出所"
          note="人手で確定した値を取り下げると、機械生成の値がそのまま戻ります（人手の値で機械生成の値は消えません）。"
        >
          {conflicted ? (
            <div className="vd-notice" role="alert">
              <p style={{ marginTop: 0 }}>
                このシートは他の画面で更新されています。最新を読み込んでから
                やり直してください。
              </p>
              <Button
                onClick={() => {
                  setUserErrors([]);
                  reload();
                }}
              >
                最新を読み込む
              </Button>
            </div>
          ) : userErrors.length === 0 ? null : (
            // field が付いた userErrors も必ずこの区画に出す
            <div className="vd-notice vd-notice--error" role="alert">
              {userErrors.map((error) => (
                <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                  {error.message}
                </p>
              ))}
            </div>
          )}
          {saved === null ? null : (
            <div className="vd-notice" role="status">
              <p style={{ margin: 0 }}>{saved}</p>
            </div>
          )}

          <div className="vd-stack">
            {groups.map((group) => (
              <section key={group} aria-label={`属性群 ${group}`}>
                <div className="vd-card-title">{group}</div>
                {sheet.attributes
                  .filter((attribute) => attribute.group === group)
                  .map((attribute) => (
                    <AttributeRow
                      // 読み直したら下書きも最新から始める
                      key={`${attribute.attribute}-${sheet.version}`}
                      attribute={attribute}
                      axes={axes}
                      version={sheet.version}
                      busy={updateState.fetching}
                      onSubmit={submit}
                    />
                  ))}
              </section>
            ))}
          </div>
        </Card>

        <Card title="商品ページ">
          <p style={{ margin: 0 }}>
            <Link
              to={`/products/${encodeURIComponent(productId ?? "")}#product-page`}
            >
              商品ページの本文と JSON-LD を見る
            </Link>
          </p>
        </Card>

        <Card
          title="属性スキーマと統制語彙"
          note="どの属性群にどの属性があるか、値域にどの語があるかは、この画面からは変えられません。"
        >
          <p style={{ margin: 0 }}>
            <Link to="/knowledge/schema">属性スキーマと統制語彙を見る</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
